import type { TimeEvent } from "../../band-data/types";

/** How far the time is outside the span; 0 inside it. */
function distanceMs({ startMs, endMs }: TimeEvent, timeMs: number): number {
    if (timeMs < startMs) return startMs - timeMs;
    return endMs === null ? 0 : Math.max(timeMs - endMs, 0);
}

/** The span under the time, or the nearest one whose padding to minMs reaches it. */
function spanAt(events: TimeEvent[], timeMs: number, minMs: number): TimeEvent | undefined {
    const hits = events.filter((event) => {
        const padMs = event.endMs === null ? 0 : Math.max(minMs - (event.endMs - event.startMs), 0) / 2;
        return distanceMs(event, timeMs) <= padMs;
    });
    return hits.sort((a, b) => distanceMs(a, timeMs) - distanceMs(b, timeMs))[0];
}

/** The candidate that overlaps the span most; none when they only touch. */
export function mostOverlapping(
    span: TimeEvent,
    candidates: TimeEvent[],
    endOf: (event: TimeEvent) => number,
): TimeEvent | undefined {
    const overlap = (candidate: TimeEvent) =>
        Math.min(endOf(span), endOf(candidate)) - Math.max(span.startMs, candidate.startMs);
    return candidates.filter((candidate) => overlap(candidate) > 0).sort((a, b) => overlap(b) - overlap(a))[0];
}

// Finite, so two running spans still compare.
const openEnded = (event: TimeEvent) => event.endMs ?? Number.MAX_SAFE_INTEGER;

/** The run under the time: the span hit plus the other source's span it overlaps most. A span shorter than minMs hits as minMs wide. */
export function findSpansAt(
    scheduled: TimeEvent[],
    actual: TimeEvent[],
    timeMs: number,
    minMs = 0,
): { scheduled?: TimeEvent; actual?: TimeEvent } {
    const scheduledAt = spanAt(scheduled, timeMs, minMs);
    const actualAt = spanAt(actual, timeMs, minMs);
    return {
        scheduled: scheduledAt ?? (actualAt && mostOverlapping(actualAt, scheduled, openEnded)),
        actual: actualAt ?? (scheduledAt && mostOverlapping(scheduledAt, actual, openEnded)),
    };
}
